import React, { useState, useEffect, useCallback, useRef } from 'react';
import TextField from '@mui/material/TextField';
import { useStepperContext } from '../../context/StepperContext';
import { useLoginContext } from '../../../../../contexts/LoginContext';
import { Box, Button } from '@mui/material';
import { stepperStyles } from '../../../shared/styles';

const ProfileName: React.FC = () => {
  const {
    user,
    selectedProfileIndex,
    updateProfile,
    setUser,
    setSelectedProfileIndex,
  } = useLoginContext();
  const { updateStepValidation, setIsNewProfile } = useStepperContext();
  const selectedProfile = user?.extProfiles[selectedProfileIndex ?? 0];
  const [helperText, setHelperText] = useState<string>('');
  const textFieldRef = useRef<HTMLInputElement>(null);

  const validateProfileName = useCallback(
    (name: string) => {
      const isBlank = name.trim() === '';
      const isDefaultName = name.trim() === 'New Profile';
      const isDuplicate = user?.extProfiles.some(
        (profile, index) =>
          index !== selectedProfileIndex &&
          profile.name?.toLowerCase() === name.toLowerCase()
      );

      if (isBlank) {
        setHelperText(
          'Profile name cannot be blank. Enter a name to continue.'
        );
      } else if (isDefaultName) {
        setHelperText('Please enter a different profile name to continue.');
      } else if (isDuplicate) {
        setHelperText(
          'Profile name already exists. Choose a unique name to continue.'
        );
      } else {
        setHelperText('');
      }
    },
    [user?.extProfiles, selectedProfileIndex, setHelperText]
  );

  // Validate profile name on component load
  useEffect(() => {
    if (selectedProfile?.name) {
      validateProfileName(selectedProfile.name);
    }
  }, [
    selectedProfile?.name,
    selectedProfileIndex,
    user?.extProfiles,
    updateStepValidation,
    validateProfileName,
  ]);

  // Maintain focus when helper text changes (stepper might appear/disappear)
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (
        textFieldRef.current &&
        document.activeElement !== textFieldRef.current
      ) {
        textFieldRef.current.focus();
      }
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [helperText]);

  const handleCancelNewProfile = () => {
    if (selectedProfileIndex !== undefined && user?.extProfiles) {
      // Remove the temp profile at the selected index
      const updatedProfiles = user.extProfiles.filter(
        (_, index) => index !== selectedProfileIndex
      );
      const updatedUser = {
        ...user,
        extProfiles: updatedProfiles,
      };
      setUser(updatedUser);

      // Set selected index to 0 or -1 if no profiles remain
      if (setSelectedProfileIndex) {
        setSelectedProfileIndex(updatedProfiles.length > 0 ? 0 : undefined);
      }

      // Clear any validation errors
      setHelperText('');
      if (setIsNewProfile) {
        setIsNewProfile(false);
      }
    }
  };

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newName = e.target.value;
    if (selectedProfile) {
      validateProfileName(newName);

      const updatedProfile = {
        ...selectedProfile,
        name: newName,
      };
      updateProfile(updatedProfile);
    }
  };

  return (
    <Box sx={{ display: 'flex', gap: 1, width: '100%', height: '100%', mt: 2 }}>
      <TextField
        fullWidth
        label={'New Profile Name'}
        value={selectedProfile?.name || ''}
        onChange={onChange}
        variant="outlined"
        size="small"
        helperText={helperText}
        error={!!helperText}
        ref={textFieldRef}
        autoFocus
      />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          height: '100%',
          textAlign: 'right',
          gap: 1,
          flex: 0,
        }}
      >
        <Button
          variant="outlined"
          onClick={handleCancelNewProfile}
          color="error"
          sx={{
            ...stepperStyles.secondaryButton,
          }}
        >
          Cancel
        </Button>
      </Box>
    </Box>
  );
};

export default ProfileName;
